import * as Types from '@aeppic/types'

import { buildDeferred, Deferred } from '@aeppic/shared/defer'
import { waitForNextTick } from '@aeppic/shared/wait-for-tick'

import { getDocumentId } from '../model/index.js'
import type { Change } from '../model/change-tracker.js'
import type { IResolvedLockRights, IServerAdapter } from '../shared/types/server-adapter.js'
import type { Features } from './features.js'

export const RESOLVE_LOCK_RIGHTS_HAS_RIGHT_CACHE_FEATURE = 'resolve-lock-rights-has-right-cache'

const MAX_RESOLVE_LOCK_RIGHTS_BATCH_SIZE = 500

type ResolvedLockAccess = {
  isOpen: boolean
  rightIds: Set<string>
}

type LockSlice = Types.Reference[]
type LockCacheEntry = ResolvedLockAccess | Promise<ResolvedLockAccess>

type QueuedLockRequest = {
  deferred: Deferred<ResolvedLockAccess>
  causalDocumentIds: Set<string>
  generation: number
}

type AeppicLike = {
  Features: Features
  Auth?: {
    accountId?: string | null
  }
  get(identifier: Types.IdOrReference, options?: any): Promise<Types.Document>
}

function isNonEmptyLockSlice(value: Types.Reference[] | null | undefined): value is Types.Reference[] {
  return Array.isArray(value) && value.length > 0
}

function getRelevantLockSlices(document: Types.Document): LockSlice[] {
  const inheritedLockSlices = (document.inheritedLocks ?? []).filter(isNonEmptyLockSlice)
  const directLocks = isNonEmptyLockSlice(document.locks) ? [document.locks] : []
  return [...inheritedLockSlices, ...directLocks]
}

function hasLockSliceChanged(
  nextLocks?: Types.Reference[] | null,
  previousLocks?: Types.Reference[] | null
): boolean {
  const nextLockIds = Array.isArray(nextLocks) ? nextLocks.map(lock => lock?.id).filter(Boolean) : []
  const previousLockIds = Array.isArray(previousLocks) ? previousLocks.map(lock => lock?.id).filter(Boolean) : []

  if (nextLockIds.length !== previousLockIds.length) {
    return true
  }

  return nextLockIds.some((lockId, index) => previousLockIds[index] !== lockId)
}

export function shouldInvalidateForChange(change: Change | { document?: Types.Document, type?: string } | null | undefined, options: { ignoreInitialPlacement?: boolean } = {}): boolean {
  const document = change?.document
  const type = change?.type

  if (!document) {
    return false
  }

  if (document.id === 'root' || type === 'deleted-hard') {
    return true
  }

  const inspectPlacement = Boolean(document.previous) || !options.ignoreInitialPlacement
  if (inspectPlacement && document.p !== document.previous?.p) {
    return true
  }

  if (inspectPlacement && hasLockSliceChanged(document.locks, document.previous?.locks)) {
    return true
  }

  const formId = document.f?.id

  return formId === 'group'
    || formId === 'key-form'
    || formId === 'lock-form'
    || formId === 'command-execution-right-form'
    || formId === 'role'
    || formId === 'form'
    || formId === 'account'
}

function isPendingLockAccess(entry: LockCacheEntry | undefined): entry is Promise<ResolvedLockAccess> {
  return entry instanceof Promise
}

export class ResolveLockRightsHasRightCache {
  private _cacheKey: string | null = null
  private _generation = 0
  private _locks = new Map<string, LockCacheEntry>()
  private _queuedLockRequests = new Map<string, QueuedLockRequest>()
  private _batchFlushScheduled = false
  private _batchFlushInProgress = false

  constructor(
    private _aeppic: AeppicLike,
    private _serverAdapter: IServerAdapter,
  ) {}

  clearIfAffected(change: Change | { document?: Types.Document, type?: string }): boolean {
    if (!shouldInvalidateForChange(change)) {
      return false
    }

    return this.clear()
  }

  clear(): boolean {

    this._generation += 1

    for (const entry of this._locks.values()) {
      if (isPendingLockAccess(entry)) {
        continue
      }
    }

    for (const queuedRequest of this._queuedLockRequests.values()) {
      queuedRequest.deferred.reject(new Error('Resolved lock rights cache cleared'))
    }

    this._cacheKey = null
    this._locks.clear()
    this._queuedLockRequests.clear()
    this._batchFlushScheduled = false
    this._batchFlushInProgress = false
    return true
  }

  async hasRight(target: Types.IdOrReference, desiredRights?: Types.IdOrReference[]): Promise<boolean> {
    if (!this._aeppic.Features.isEnabled(RESOLVE_LOCK_RIGHTS_HAS_RIGHT_CACHE_FEATURE)) {
      throw new Error('ResolveLockRightsHasRightCache called while feature is disabled')
    }

    const targetId = getDocumentId(target)

    if (!targetId) {
      return false
    }

    const targetDocument = await this._aeppic.get(targetId)

    if (!targetDocument) {
      return false
    }

    const lockSlices = getRelevantLockSlices(targetDocument)

    if (lockSlices.length === 0) {
      return true
    }

    const lockIds = [...new Set(lockSlices.flat().map(lockRef => lockRef.id))]
    const lockAccess = await this._resolveLockAccess(lockIds, targetId)

    for (const lockSlice of lockSlices) {
      const someLockOpensThisSlice = lockSlice.some(lockRef => lockAccess.get(lockRef.id)?.isOpen === true)

      if (!someLockOpensThisSlice) {
        return false
      }
    }

    if (!desiredRights) {
      return true
    }

    const desiredRightIds = desiredRights.map(getDocumentId)

    if (desiredRightIds.length === 0) {
      throw new Error('No rights specified. If read access is desired do not pass in any rights (null/undefined)')
    }

    if (desiredRightIds.some(rightId => !rightId)) {
      throw new Error('Invalid right specified')
    }

    const closestLockSlice = lockSlices[lockSlices.length - 1]
    const closestSliceRightIds = new Set<string>()

    for (const lockRef of closestLockSlice) {
      const resolvedLock = lockAccess.get(lockRef.id)

      if (!resolvedLock) {
        return false
      }

      for (const rightId of resolvedLock.rightIds) {
        closestSliceRightIds.add(rightId)
      }
    }

    return desiredRightIds.some(rightId => closestSliceRightIds.has(rightId))
  }

  private async _resolveLockAccess(lockIds: string[], targetId: string): Promise<Map<string, ResolvedLockAccess>> {
    await Promise.all(lockIds.map(lockId => Promise.resolve(this._getOrQueueLock(lockId, targetId))))

    const result = new Map<string, ResolvedLockAccess>()

    for (const lockId of lockIds) {
      const resolvedLock = this._locks.get(lockId)

      if (!resolvedLock || isPendingLockAccess(resolvedLock)) {
        throw new Error(`Missing resolved lock access for ${lockId}`)
      }

      result.set(lockId, resolvedLock)
    }

    return result
  }

  private _getOrQueueLock(lockId: string, targetId: string): Promise<ResolvedLockAccess> | ResolvedLockAccess {
    const cached = this._locks.get(lockId)

    if (cached) {
      if (isPendingLockAccess(cached)) {
        this._queuedLockRequests.get(lockId)?.causalDocumentIds.add(targetId)
        return cached
      }

      return cached
    }

    const deferred = buildDeferred<ResolvedLockAccess>()

    this._locks.set(lockId, deferred.promise)
    this._queuedLockRequests.set(lockId, {
      deferred,
      causalDocumentIds: new Set([targetId]),
      generation: this._generation,
    })

    this._scheduleBatchFlush()

    return deferred.promise
  }

  private _scheduleBatchFlush() {
    if (this._batchFlushScheduled) {
      return
    }

    this._batchFlushScheduled = true

    waitForNextTick().then(async () => {
      this._batchFlushScheduled = false

      if (this._batchFlushInProgress || this._queuedLockRequests.size === 0) {
        return
      }

      this._batchFlushInProgress = true

      try {
        await this._flushQueuedLockRequests()
      } finally {
        this._batchFlushInProgress = false

        if (this._queuedLockRequests.size > 0) {
          this._scheduleBatchFlush()
        }
      }
    })
  }

  private async _flushQueuedLockRequests() {
    while (this._queuedLockRequests.size > 0) {
      const batchEntries = [...this._queuedLockRequests.entries()].slice(0, MAX_RESOLVE_LOCK_RIGHTS_BATCH_SIZE)
      const batchGeneration = batchEntries[0]?.[1]?.generation ?? this._generation

      for (const [lockId] of batchEntries) {
        this._queuedLockRequests.delete(lockId)
      }

      if (batchGeneration !== this._generation) {
        this._rejectBatchEntries(batchEntries, new Error('Resolved lock rights batch invalidated by cache clear'))
        continue
      }

      const lockIds = batchEntries.map(([lockId]) => lockId)
      const causalDocumentIds = [...new Set(batchEntries.flatMap(([, request]) => [...request.causalDocumentIds]))]

      try {
        const resolvedLockRights = await this._serverAdapter.resolveLockRights(lockIds, {
          causalDocumentId: causalDocumentIds.length === 1 ? causalDocumentIds[0] : null,
        })

        if (batchGeneration !== this._generation) {
          this._rejectBatchEntries(batchEntries, new Error('Resolved lock rights batch invalidated while awaiting server response'))
          this.clear()
          continue
        }

        const resolvedLockAccess = this._buildResolvedLockAccessById(resolvedLockRights, lockIds)

        for (const [lockId, request] of batchEntries) {
          const resolvedLock = resolvedLockAccess.get(lockId)
          const currentCacheEntry = this._locks.get(lockId)

          if (!resolvedLock || !currentCacheEntry || !isPendingLockAccess(currentCacheEntry)) {
            continue
          }

          this._locks.set(lockId, resolvedLock)
          request.deferred.resolve(resolvedLock)
        }
      } catch (error) {
        for (const [lockId, request] of batchEntries) {
          if (this._locks.get(lockId) === request.deferred.promise) {
            this._locks.delete(lockId)
          }

          request.deferred.reject(error)
        }
      }
    }
  }

  private _rejectBatchEntries(
    batchEntries: [string, QueuedLockRequest][],
    error: Error
  ) {
    for (const [lockId, request] of batchEntries) {
      if (this._locks.get(lockId) === request.deferred.promise) {
        this._locks.delete(lockId)
      }

      request.deferred.reject(error)
    }
  }

  private _buildResolvedLockAccessById(
    resolvedLockRights: IResolvedLockRights,
    requestedLockIds: string[]
  ): Map<string, ResolvedLockAccess> {
    if (!resolvedLockRights || typeof resolvedLockRights.authEpoch !== 'string' || !Array.isArray(resolvedLockRights.openedLockIds)) {
      throw new Error('Invalid resolveLockRights response')
    }

    const newCacheKey = `${resolvedLockRights.principalId ?? 'anonymous'}:${resolvedLockRights.authEpoch}`

    if (this._cacheKey && this._cacheKey !== newCacheKey) {
      for (const [lockId, cacheEntry] of this._locks.entries()) {
        if (isPendingLockAccess(cacheEntry)) {
          continue
        }

        this._locks.delete(lockId)
      }
    }

    this._cacheKey = newCacheKey

    const openedLockIds = new Set(resolvedLockRights.openedLockIds)
    const resolvedLockAccessById = new Map<string, ResolvedLockAccess>()

    for (const lockId of requestedLockIds) {
      resolvedLockAccessById.set(lockId, {
        isOpen: openedLockIds.has(lockId),
        rightIds: new Set(resolvedLockRights.lockRights?.[lockId] ?? []),
      })
    }

    return resolvedLockAccessById
  }
}
